import { useEffect, useMemo, useState, useCallback, useContext } from 'react';

import type { GridSortModel } from '@mui/x-data-grid';
import debounce from 'lodash/debounce';

import queryString from 'query-string';
import { useHistory } from 'react-router-dom';
import { AxiosResponse } from 'axios';

import {
  AGE_MARKS_OPTIONS,
  CERTIFICATE_FILTERS,
  DOCUMENT_STATUSES_ARR,
  GRID_PAGE_SIZE,
  MAX_WORKER_AGE,
  MIN_WORKER_AGE,
  QUALIFICATION_STATUSES,
  RTW_VALUES,
  TABLE_OFFSET_DELAY,
} from '@vyce/core/src/constants';
import {
  ChatUser,
  FilterSection,
  Nationality,
  Tag,
  TagGroups,
  Team,
  TeamMember,
} from '@vyce/core/src/types';
import { GetCompanyTeamsMembersDataRequest, TeamCertificateFilter } from '@vyce/core/src/api/types';
import { fetchNationalitiesRequest, fetchTagsRequest } from '@vyce/core/src/api/handbook';
import { getSurveyList } from '@vyce/core/src/api/survey';
import { generateQueryParamsFromObj } from '@vyce/core/src/utils/url';
import { isJson, isNil } from '@vyce/core/src/utils';
import { formatSortModel } from '@vyce/core/src/utils/sorting';
import {
  postExportQualifications,
  deleteTeamMembersFromModuleRequest,
  getTeamQualificationsRequest,
} from '@vyce/core/src/api/teams';
import { useDebounceValue } from '@vyce/core/src/hooks/useDebounceValue';
import { useBooleanState } from '@vyce/core/src/hooks/useBooleanState';
import { useIsMounted } from '@vyce/core/src/hooks/useIsMounted';
import { useTable } from '@vyce/core/src/hooks/useTable';
import { NotificationContext } from '@vyce/core/src/contexts/notificationContext';
import useChatRequests from '@vyce/core/src/hooks/useChatRequests';

import { getTeamsFilter } from '../utils';

interface Props {
  companyId?: string;
  teams: Team[] | null;
  chatCreator?: ChatUser;
  getAllTeamMembersRequest: (
    companyId: string,
    requestData: GetCompanyTeamsMembersDataRequest
  ) => Promise<AxiosResponse>;
}

const defaultSortModel: GridSortModel = [{ field: 'registration_date', sort: 'desc' }];

export const useTeamMembersData = ({ companyId, getAllTeamMembersRequest, teams, chatCreator }: Props) => {
  const { handleServerError, showNotification } = useContext(NotificationContext);
  const isMounted = useIsMounted();
  const [loading, setLoading] = useState<boolean>(false);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [roles, setRoles] = useState<Tag[]>([]);
  const [qualifications, setQualifications] = useState<string[]>([]);
  const [nationalities, setNationalities] = useState<Nationality[]>([]);
  const [questions, setQuestions] = useState<string[]>([]);
  const [exportButtonLoading, setExportButtonLoading] = useState<boolean>(false);
  const [removeLoading, setRemoveLoading] = useState<boolean>(false);
  const [filters, setFilters] = useState<any>();
  const [closeGridAction, setCloseGridAction] = useState<boolean>(false);
  const [isConfirmDialogOpen, openConfirmDialog, closeConfirmDialog] = useBooleanState(false);
  const [isNotifyDialogOpen, openNotifyDialog, closeNotifyDialog] = useBooleanState(false);
  const [userToRemove, setUserToRemove] = useState<TeamMember>();
  const { createPrivateChat } = useChatRequests();

  const {
    sortModel,
    offset,
    substring,
    total,
    setTotal,
    setOffset,
    handleSortModelChange,
    handlePageChange,
    handleSearchChange,
  } = useTable({ defaultSortModel });

  const dOffset = useDebounceValue(offset, TABLE_OFFSET_DELAY);

  const history = useHistory();
  const teamsFilterOptions = getTeamsFilter(teams ?? []);

  const goToChat = async (timeMember: TeamMember) => {
    if (!chatCreator || !companyId) {
      return;
    }
    await createPrivateChat({
      creator: chatCreator,
      chatUser: {
        uid: timeMember.user_id,
        name: `${timeMember.first_name} ${timeMember.last_name}`,
        email: timeMember.email,
        phone: timeMember.phone,
        avatarUrl: timeMember.avatar,
      },
      companyId,
    });
  };

  const fetchQualifications = async () => {
    if (!companyId) return;
    try {
      const res = await getTeamQualificationsRequest(companyId);
      if (!isMounted.current) return;
      setQualifications(res.data.items);
    } catch (e) {
      console.error(e);
    }
  };

  const fetchJobsAndNationalities = async () => {
    try {
      const resRoles = await fetchTagsRequest({ group: TagGroups.ROLE, short: false });
      const resNationalities = await fetchNationalitiesRequest();
      if (!isMounted.current) return;
      setRoles(resRoles.data.items?.map(i => i.name) as any);
      setNationalities(resNationalities.data.items?.map(i => i.name) as any);
    } catch (e) {
      console.error(e);
    }
  };

  const fetchSurveysQuestions = async () => {
    try {
      const surveysQuestions = await getSurveyList();
      if (!isMounted.current) return;
      setQuestions(surveysQuestions.data.items?.map(i => i.name));
    } catch (e) {
      console.error(e);
    }
  };

  const filtersSections: FilterSection[] = useMemo(
    () => [
      {
        title: 'Health & Safety',
        expanded: true,
        filters: [
          {
            type: 'select',
            multiple: false,
            label: 'Select medical condition',
            field: 'survey_tag',
            values: ['', ...questions],
            defaultValue: '',
          },
        ],
      },
      teamsFilterOptions,

      {
        title: 'Checks',
        expanded: true,
        filters: [
          {
            type: 'select',
            multiple: false,
            label: 'ID Status',
            field: 'id_status',
            values: ['', ...DOCUMENT_STATUSES_ARR],
            defaultValue: '',
          },
          {
            type: 'select',
            multiple: false,
            label: 'RTW Status',
            field: 'rtw_status',
            values: [
              '',
              RTW_VALUES.UNVERIFIED,
              RTW_VALUES.NO_RTW,
              RTW_VALUES.LIMITED,
              RTW_VALUES.EXPIRING,
              RTW_VALUES.INDEFINITE,
            ],
            defaultValue: '',
          },
        ],
      },
      {
        title: 'Worker Profiles',
        expanded: true,
        filters: [
          {
            type: 'select',
            multiple: false,
            label: 'Job Role',
            field: 'role',
            values: ['', ...roles],
            defaultValue: '',
          },
          {
            type: 'select',
            multiple: false,
            label: 'Nationality',
            field: 'nationality',
            values: ['', ...nationalities],
            defaultValue: '',
          },
          {
            type: 'select',
            multiple: false,
            label: 'Gender',
            field: 'gender',
            values: ['', 'male', 'female'],
            defaultValue: '',
          },
          {
            type: 'slider',
            label: 'Age',
            field: 'age',
            dataType: 'object',
            min: MIN_WORKER_AGE,
            max: MAX_WORKER_AGE,
            defaultValue: { gte: MIN_WORKER_AGE, lte: MAX_WORKER_AGE },
            marks: AGE_MARKS_OPTIONS,
          },
        ],
      },
      {
        title: 'Certificates',
        expanded: true,
        filters: [
          {
            type: 'autocomplete',
            multiple: true,
            label: 'Certificate',
            field: 'certificate',
            values: CERTIFICATE_FILTERS,
            defaultValue: [],
          },
        ],
      },
      {
        title: 'Qualifications',
        expanded: true,
        filters: [
          {
            type: 'select',
            multiple: false,
            label: 'Qualification',
            field: 'qualification',
            values: qualifications,
            defaultValue: '',
          },
          {
            type: 'select',
            multiple: false,
            label: 'Qualification Status',
            field: 'qualification_status',
            values: QUALIFICATION_STATUSES,
            defaultValue: '',
          },
        ],
      },
    ],
    [roles, nationalities, qualifications]
  );

  const resetCloseStatus = () => {
    setCloseGridAction(true);
    setTimeout(() => setCloseGridAction(false), 100);
  };

  const removeFromModule = async () => {
    if (!userToRemove || !companyId) {
      return;
    }
    try {
      setRemoveLoading(true);
      const res = await deleteTeamMembersFromModuleRequest(companyId, {
        members: [userToRemove.user_id],
      });
      setTeamMembers(value => value.filter(user => user.user_id !== userToRemove.user_id));
      closeConfirmDialog();
      showNotification({ message: res.data.message, options: { variant: 'success' } });
    } catch (e) {
      handleServerError(e);
    } finally {
      setRemoveLoading(false);
    }
  };

  const handleRemoveTeamMemberClick = (user: TeamMember) => {
    setUserToRemove(user);
    openConfirmDialog();
  };

  const handleRemoveClick = (user: TeamMember) => {
    resetCloseStatus();
    handleRemoveTeamMemberClick(user);
  };

  const getTeamMembers = useCallback(async () => {
    if (!companyId || isNil(dOffset)) {
      return;
    }
    try {
      setLoading(true);

      const requestData: GetCompanyTeamsMembersDataRequest = {
        offset: dOffset,
        limit: GRID_PAGE_SIZE,
        substring,
        order_by: formatSortModel<TeamMember>(sortModel),
        ...prepareBackendFilters(filters ?? {}),
      };

      const {
        data: { items, count },
      } = await getAllTeamMembersRequest(companyId, requestData);
      if (!isMounted.current) return;
      setLoading(false);
      setTeamMembers(items);
      setTotal(count);
    } catch (e) {
      if (!isMounted.current) return;
      setLoading(true);
      handleServerError(e);
    }
  }, [companyId, filters, dOffset, sortModel, substring]);

  const exportQualifications = useCallback(async () => {
    if (!companyId) {
      return;
    }
    try {
      setExportButtonLoading(true);
      const team_id = filters?.teamId ?? teams?.find(t => t.name === filters?.team_name)?.uuid;
      await postExportQualifications({
        company_id: companyId,
        data: {
          substring: substring,
          survey_tag: filters?.survey_tag,
          age:
            filters?.age?.gte !== MIN_WORKER_AGE || filters?.age?.lte !== MAX_WORKER_AGE
              ? filters?.age
              : undefined,
          gender: filters?.gender,
          nationality: filters?.nationality,
          role: filters?.role,
          rtw_status: filters?.rtw_status,
          id_status: filters?.id_status,
          team_ids: team_id ? [team_id] : undefined,
          certificate: filters?.certificate?.map((c: TeamCertificateFilter) => ({
            type: c.type,
            status: c.status,
          })),
          order_by: formatSortModel(sortModel),
          limit: 3000,
        },
      });
      showNotification({
        message:
          'The Qualification Matrix of your workforce is being prepared. It will be sent to your email address soon!',
        options: { variant: 'info' },
      });
      setExportButtonLoading(false);
    } catch (e) {
      console.error(e);
      setExportButtonLoading(false);
      handleServerError(e);
    }
  }, [companyId, filters, substring, sortModel, teams]);

  const handleFilterChange = debounce((filters: any) => {
    history.push({
      pathname: history.location.pathname,
      search: generateQueryParamsFromObj(filters),
    });
  }, 300);

  const formatFilters = (filters: any) => {
    let formattedFilters = { ...filters };
    Object.keys(formattedFilters).forEach(key => {
      const filterItem = formattedFilters[key];
      if (!filterItem) {
        delete formattedFilters[key];
      }
      if (isJson(filterItem)) {
        formattedFilters[key] = JSON.parse(filterItem);
      }
    });

    return formattedFilters;
  };

  const prepareBackendFilters = (filters: any) => {
    const teamId = filters?.teamId ?? teams?.find(team => team.name === filters?.team_name)?.uuid;
    return {
      ...filters,
      age:
        filters?.age?.gte !== MIN_WORKER_AGE || filters?.age?.lte !== MAX_WORKER_AGE
          ? filters?.age
          : undefined,
      team_id: teamId,
    };
  };

  useEffect(() => {
    fetchJobsAndNationalities();
    fetchQualifications();
    fetchSurveysQuestions();
  }, []);

  useEffect(() => {
    if (history.location.search) {
      const { page, ...filters } = queryString.parse(history.location.search, { parseNumbers: true });
      const formattedFilters = formatFilters(filters);
      if (formattedFilters.teamId && teams?.length && !formattedFilters.team_name) {
        const team = teams.find(t => t.uuid === formattedFilters.teamId);
        if (team) formattedFilters.team_name = team.name;
      }
      setFilters(formattedFilters);
    }
  }, [history.location.search, teams]);

  useEffect(() => {
    getTeamMembers();
  }, [getTeamMembers]);

  return {
    loading,
    substring,
    teamMembers,
    total,
    sortModel,
    exportButtonLoading,
    handleSearchChange,
    filtersSections,
    filters,
    handleFilterChange,
    isConfirmDialogOpen,
    userToRemove,
    closeGridAction,
    isNotifyDialogOpen,
    closeNotifyDialog,
    setOffset,
    handleSortModelChange,
    handlePageChange,
    closeConfirmDialog,
    removeFromModule,
    removeLoading,
    handleRemoveClick,
    exportQualifications,
    goToChat,
  };
};
